/**
 * AuthManager — Login/Register modal + JWT auth state management.
 * Follows the same DOM-construction pattern as FaqModal and HeroThicknessModal.
 */
export default class AuthManager {
  constructor() {
    this.API_BASE = 'https://cdr-auth-worker.vetrongmbh.workers.dev';
    this.user = null;
    this.token = null;
    this.observers = [];
    this.overlay = null;
    this.mode = 'login'; // 'login' | 'register'

    this._loadFromStorage();
    this._buildModal();
    this._bindEvents();

    // Validate existing token on load
    if (this.token) {
      this._validateToken();
    } else {
      this._notifyObservers(false);
    }
  }

  // --- Public API ---

  isLoggedIn() { return !!this.user; }
  getUser() { return this.user; }

  onAuthChange(callback) {
    this.observers.push(callback);
    // Fire immediately with current state
    callback(this.isLoggedIn(), this.user);
  }

  openLoginModal() {
    this.mode = 'login';
    this._updateModalView();
    this.overlay.classList.remove('hidden');
  }

  openRegisterModal() {
    this.mode = 'register';
    this._updateModalView();
    this.overlay.classList.remove('hidden');
  }

  closeModal() {
    this.overlay.classList.add('hidden');
    this._clearError();
  }

  async login(email, password) {
    this._clearError();
    this._setLoading(true);
    try {
      const res = await fetch(`${this.API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) {
        this._showError(data.error || 'Anmeldung fehlgeschlagen.');
        return false;
      }
      this.token = data.token;
      this.user = data.user;
      this._saveToStorage();
      this._notifyObservers(true);
      this.closeModal();
      this._updateAuthButtons();
      return true;
    } catch (e) {
      this._showError('Verbindungsfehler. Bitte versuche es erneut.');
      return false;
    } finally {
      this._setLoading(false);
    }
  }

  async register(email, password, name, company) {
    this._clearError();
    this._setLoading(true);
    try {
      const res = await fetch(`${this.API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, company })
      });
      const data = await res.json();
      if (!res.ok) {
        this._showError(data.error || 'Registrierung fehlgeschlagen.');
        return false;
      }
      this.token = data.token;
      this.user = data.user;
      this._saveToStorage();
      this._notifyObservers(true);
      this.closeModal();
      this._updateAuthButtons();
      return true;
    } catch (e) {
      this._showError('Verbindungsfehler. Bitte versuche es erneut.');
      return false;
    } finally {
      this._setLoading(false);
    }
  }

  logout() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('cdr-auth-token');
    localStorage.removeItem('cdr-auth-user');
    this._notifyObservers(false);
    this._updateAuthButtons();
  }

  // --- Private ---

  _loadFromStorage() {
    try {
      this.token = localStorage.getItem('cdr-auth-token');
      const userStr = localStorage.getItem('cdr-auth-user');
      this.user = userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      this.token = null;
      this.user = null;
    }
  }

  _saveToStorage() {
    localStorage.setItem('cdr-auth-token', this.token);
    localStorage.setItem('cdr-auth-user', JSON.stringify(this.user));
  }

  async _validateToken() {
    try {
      const res = await fetch(`${this.API_BASE}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${this.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        this.user = data.user;
        this._saveToStorage();
        this._notifyObservers(true);
      } else {
        this.logout();
      }
    } catch (e) {
      // Network error — keep local state, don't log out
      if (this.user) {
        this._notifyObservers(true);
      }
    }
    this._updateAuthButtons();
  }

  _notifyObservers(isLoggedIn) {
    this.observers.forEach(cb => cb(isLoggedIn, this.user));
  }

  _buildModal() {
    // Overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'auth-overlay hidden';
    this.overlay.id = 'auth-overlay';

    // Modal card
    const modal = document.createElement('div');
    modal.className = 'auth-modal';

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'auth-close';
    closeBtn.innerHTML = '<i class="fas fa-times"></i>';
    closeBtn.addEventListener('click', () => this.closeModal());

    // Title
    this.titleEl = document.createElement('h2');
    this.titleEl.className = 'auth-title';

    // Error
    this.errorEl = document.createElement('div');
    this.errorEl.className = 'auth-error';

    // Login form
    this.loginForm = this._buildLoginForm();

    // Register form
    this.registerForm = this._buildRegisterForm();

    // Logged-in view (user info + logout)
    this.userView = this._buildUserView();

    modal.appendChild(closeBtn);
    modal.appendChild(this.titleEl);
    modal.appendChild(this.errorEl);
    modal.appendChild(this.loginForm);
    modal.appendChild(this.registerForm);
    modal.appendChild(this.userView);

    this.overlay.appendChild(modal);

    // Click overlay to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.closeModal();
    });

    document.body.appendChild(this.overlay);
  }

  _buildLoginForm() {
    const form = document.createElement('div');
    form.className = 'auth-form';
    form.id = 'auth-login-form';

    form.innerHTML = `
      <div class="auth-form-group">
        <label class="auth-form-label">E-Mail</label>
        <input class="auth-form-input" type="email" id="auth-login-email" placeholder="name@firma.ch" autocomplete="email">
      </div>
      <div class="auth-form-group">
        <label class="auth-form-label">Passwort</label>
        <input class="auth-form-input" type="password" id="auth-login-password" placeholder="Passwort" autocomplete="current-password">
      </div>
      <button class="auth-submit" id="auth-login-btn">
        <span class="auth-submit-text">Anmelden</span>
        <span class="auth-submit-spinner hidden"><i class="fas fa-circle-notch fa-spin"></i></span>
      </button>
      <div class="auth-switch">
        Noch kein Konto? <a href="#" id="auth-to-register">Registrieren</a>
      </div>
    `;

    return form;
  }

  _buildRegisterForm() {
    const form = document.createElement('div');
    form.className = 'auth-form hidden';
    form.id = 'auth-register-form';

    form.innerHTML = `
      <div class="auth-form-group">
        <label class="auth-form-label">Name</label>
        <input class="auth-form-input" type="text" id="auth-reg-name" placeholder="Vor- und Nachname" autocomplete="name">
      </div>
      <div class="auth-form-group">
        <label class="auth-form-label">Firma</label>
        <input class="auth-form-input" type="text" id="auth-reg-company" placeholder="Firma" autocomplete="organization">
      </div>
      <div class="auth-form-group">
        <label class="auth-form-label">E-Mail</label>
        <input class="auth-form-input" type="email" id="auth-reg-email" placeholder="name@firma.ch" autocomplete="email">
      </div>
      <div class="auth-form-group">
        <label class="auth-form-label">Passwort</label>
        <input class="auth-form-input" type="password" id="auth-reg-password" placeholder="Mind. 8 Zeichen" autocomplete="new-password">
      </div>
      <button class="auth-submit" id="auth-register-btn">
        <span class="auth-submit-text">Registrieren</span>
        <span class="auth-submit-spinner hidden"><i class="fas fa-circle-notch fa-spin"></i></span>
      </button>
      <div class="auth-switch">
        Bereits ein Konto? <a href="#" id="auth-to-login">Anmelden</a>
      </div>
    `;

    return form;
  }

  _buildUserView() {
    const view = document.createElement('div');
    view.className = 'auth-user-view hidden';
    view.id = 'auth-user-view';

    view.innerHTML = `
      <div class="auth-user-info">
        <div class="auth-user-avatar" id="auth-user-avatar"></div>
        <div class="auth-user-details">
          <div class="auth-user-name" id="auth-user-name"></div>
          <div class="auth-user-email" id="auth-user-email"></div>
          <div class="auth-user-company" id="auth-user-company"></div>
        </div>
      </div>
      <button class="auth-logout" id="auth-logout-btn">
        <i class="fas fa-sign-out-alt"></i>
        <span>Abmelden</span>
      </button>
    `;

    return view;
  }

  _bindEvents() {
    // Login form submit
    document.addEventListener('click', (e) => {
      if (e.target.closest('#auth-login-btn')) {
        e.preventDefault();
        const email = document.getElementById('auth-login-email').value.trim();
        const password = document.getElementById('auth-login-password').value;
        if (email && password) this.login(email, password);
      }

      if (e.target.closest('#auth-register-btn')) {
        e.preventDefault();
        const name = document.getElementById('auth-reg-name').value.trim();
        const company = document.getElementById('auth-reg-company').value.trim();
        const email = document.getElementById('auth-reg-email').value.trim();
        const password = document.getElementById('auth-reg-password').value;
        if (email && password) this.register(email, password, name, company);
      }

      if (e.target.closest('#auth-to-register')) {
        e.preventDefault();
        this.mode = 'register';
        this._updateModalView();
      }

      if (e.target.closest('#auth-to-login')) {
        e.preventDefault();
        this.mode = 'login';
        this._updateModalView();
      }

      if (e.target.closest('#auth-logout-btn')) {
        e.preventDefault();
        this.logout();
        this.closeModal();
      }
    });

    // Enter key submits forms
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      if (this.overlay.classList.contains('hidden')) return;

      if (this.mode === 'login') {
        const email = document.getElementById('auth-login-email').value.trim();
        const password = document.getElementById('auth-login-password').value;
        if (email && password) this.login(email, password);
      } else if (this.mode === 'register') {
        const name = document.getElementById('auth-reg-name').value.trim();
        const company = document.getElementById('auth-reg-company').value.trim();
        const email = document.getElementById('auth-reg-email').value.trim();
        const password = document.getElementById('auth-reg-password').value;
        if (email && password) this.register(email, password, name, company);
      }
    });
  }

  _updateModalView() {
    this._clearError();

    if (this.isLoggedIn()) {
      this.titleEl.textContent = 'Mein Konto';
      this.loginForm.classList.add('hidden');
      this.registerForm.classList.add('hidden');
      this.userView.classList.remove('hidden');

      const avatar = document.getElementById('auth-user-avatar');
      const nameEl = document.getElementById('auth-user-name');
      const emailEl = document.getElementById('auth-user-email');
      const companyEl = document.getElementById('auth-user-company');

      if (avatar) avatar.textContent = this._getInitials(this.user.name || this.user.email);
      if (nameEl) nameEl.textContent = this.user.name || '';
      if (emailEl) emailEl.textContent = this.user.email;
      if (companyEl) companyEl.textContent = this.user.company || '';
    } else if (this.mode === 'login') {
      this.titleEl.textContent = 'Anmelden';
      this.loginForm.classList.remove('hidden');
      this.registerForm.classList.add('hidden');
      this.userView.classList.add('hidden');
    } else {
      this.titleEl.textContent = 'Konto erstellen';
      this.loginForm.classList.add('hidden');
      this.registerForm.classList.remove('hidden');
      this.userView.classList.add('hidden');
    }
  }

  _updateAuthButtons() {
    // Update all auth toggle buttons (desktop + mobile)
    document.querySelectorAll('#auth-toggle, #mobile-auth-toggle').forEach(btn => {
      if (this.isLoggedIn()) {
        btn.innerHTML = `<span class="auth-avatar-sm">${this._getInitials(this.user.name || this.user.email)}</span>`;
        btn.title = this.user.name || this.user.email;
      } else {
        btn.innerHTML = '<i class="fas fa-user"></i>';
        btn.title = 'Anmelden';
      }
    });
  }

  _getInitials(str) {
    if (!str) return '?';
    const parts = str.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return str.substring(0, 2).toUpperCase();
  }

  _showError(msg) {
    this.errorEl.textContent = msg;
    this.errorEl.classList.add('visible');
  }

  _clearError() {
    this.errorEl.textContent = '';
    this.errorEl.classList.remove('visible');
  }

  _setLoading(loading) {
    const btnId = this.mode === 'login' ? 'auth-login-btn' : 'auth-register-btn';
    const btn = document.getElementById(btnId);
    if (!btn) return;
    const text = btn.querySelector('.auth-submit-text');
    const spinner = btn.querySelector('.auth-submit-spinner');
    if (loading) {
      btn.disabled = true;
      if (text) text.classList.add('hidden');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      btn.disabled = false;
      if (text) text.classList.remove('hidden');
      if (spinner) spinner.classList.add('hidden');
    }
  }
}
